import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Briefcase, Plus, FileText } from 'lucide-react';
import ProjectSetupCard from './ProjectSetupCard';
import ProjectSetupModal from './ProjectSetupModal';
import NewProjectModal from './NewProjectModal';
import ModelQuotesTable from './ModelQuotesTable';
import ModelQuotesModalNoPricing from './ModelQuotesModalNoPricing';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { Button, SearchBar } from './ui';
import { ProjectService } from '../services/projectService';
import { supabase } from '../lib/supabase';

const ProjectSetupBoard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('waiting');
  const [nextJobNumber, setNextJobNumber] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newProjectModalOpen, setNewProjectModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [allModelQuotes, setAllModelQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [allProjectsLimit, setAllProjectsLimit] = useState(50);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    // Also set a small timeout to ensure it happens after render
    const scrollTimeout = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    
    loadInitialData();
    
    return () => clearTimeout(scrollTimeout);
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [allProjects, clientsData, contactsData, allQuotesData] = await Promise.all([
        ProjectService.getAllProjects(),
        ProjectService.getClients(),
        ProjectService.getContacts(),
        ProjectService.getAllModelQuotes()
      ]);

      setProjects(allProjects);
      setClients(clientsData);
      setContacts(contactsData);
      setAllModelQuotes(allQuotesData);
      
      // Calculate next job number
      // Numbers >= 9000 are test/placeholder projects — excluding them keeps a stray
      // test project from poisoning the sequence (matches MAX_REAL_JOB_NUMBER in the
      // automation pipeline's stage2_project_setup.py)
      const jobNumbers = allProjects
        .map(p => {
          if (!p.jobNumber) return 0;
          // Extract numeric part only (ignore REV)
          const match = p.jobNumber.match(/^(\d+)/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter(num => num > 0 && num < 9000);
      
      const maxJobNumber = jobNumbers.length > 0 ? Math.max(...jobNumbers) : 0;
      setNextJobNumber(maxJobNumber + 1);
      
    } catch (err) {
      console.error('Error loading projects:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const waitingProjects = useMemo(() => {
    return projects.filter(p =>
      (p.modelingStatus === 'Startup' || !p.modelingStatus) &&
      p.projectSetupComplete !== true
    );
  }, [projects]);

  const allProjectsSorted = useMemo(() => {
    return [...projects].sort((a, b) => {
      // Sort by initialAcceptance date, most recent first
      const aDate = a.initialAcceptance ? new Date(a.initialAcceptance).getTime() : 0;
      const bDate = b.initialAcceptance ? new Date(b.initialAcceptance).getTime() : 0;
      return bDate - aDate; // Most recent first
    });
  }, [projects]);

  const searchedProjects = useMemo(() => {
    // If no search term, return projects based on active tab
    if (!searchTerm.trim()) {
      return activeTab === 'waiting' ? waitingProjects : allProjectsSorted;
    }

    // When searching, always search ALL projects regardless of tab
    const searchLower = searchTerm.toLowerCase();

    return allProjectsSorted.filter(project =>
      (project.jobNumber && project.jobNumber.toLowerCase().includes(searchLower)) ||
      (project.projectName && project.projectName.toLowerCase().includes(searchLower)) ||
      (project.clientName && project.clientName.toLowerCase().includes(searchLower)) ||
      (project.projectAddress && project.projectAddress.toLowerCase().includes(searchLower))
    );
  }, [activeTab, waitingProjects, allProjectsSorted, searchTerm]);

  // Apply truncation only to "All Projects" tab
  const displayProjects = useMemo(() => {
    if (activeTab === 'all' && allProjectsLimit !== 'all') {
      return searchedProjects.slice(0, allProjectsLimit);
    }
    return searchedProjects;
  }, [activeTab, searchedProjects, allProjectsLimit]);

  const handleCardClick = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    console.log('ProjectSetupBoard: Modal close triggered');
    console.trace('Close call stack');
    setModalOpen(false);
    setSelectedProject(null);
  };

  const handleModalSave = async () => {
    // Only refresh the specific project that was edited
    if (selectedProject?.id) {
      try {
        // Fetch just this one project with all its fields using the same query as getAllProjects
        const { data, error } = await supabase
          .from("projects")
          .select(`
            id,
            name,
            job_number,
            rev,
            modeling_status,
            modeling_state,
            billing_status,
            agreement_status,
            due_date,
            completed_date,
            projected_completion_date,
            erosion_projected_completion_date,
            erosion_due_date,
            final_projected_completion_date,
            finish_grade_due_date,
            model_builder_id,
            is_high_priority,
            client_id,
            contact_id,
            ready_for_employee_id,
            last_modified_by_email,
            last_modified_at,
            last_stage_change_date,
            stage_status,
            model_request_additional_info,
            additional_requests,
            file_formats_needed,
            initial_acceptance,
            model_request_client,
            model_request_contact,
            send_to_billing,
            approximate_acreage,
            template_filter,
            file_attachments,
            create_subitems,
            slack_channel_id,
            model_price,
            invoiced_date,
            paid_date,
            ready_to_invoice,
            po_number,
            hold_start_date,
            hold_reason,
            hold_notes,
            hold_initial_follow_up_days,
            hold_recurring_follow_up_days,
            hold_reminder_sent_dates,
            location,
            pdfs_received,
            cad_files_received,
            pdfs_received_date,
            cad_files_received_date,
            project_setup_complete,
            is_revision,
            parent_project_id,
            us_state,
            clients (
              id,
              name,
              client_of,
              state,
              trimble_formats
            ),
            contact:contacts (
              id,
              name,
              email,
              phone_number,
              role,
              client_id
            ),
            ready_for_employee:employees!ready_for_employee_id (
              id,
              name,
              email
            ),
            model_builder_employee:employees!model_builder_id (
              id,
              name,
              email
            )
          `)
          .eq('id', selectedProject.id)
          .single();

        if (error) throw error;

        if (data) {
          // Transform the data the same way ProjectService does
          const transformedProject = ProjectService.transformProjectData(data);

          setProjects(prevProjects => {
            const updated = prevProjects.map(p => p.id === transformedProject.id ? transformedProject : p);

            // Recalculate next job number from updated projects
            // (>= 9000 = test/placeholder, excluded — see loadProjects above)
            const jobNumbers = updated
              .map(p => {
                if (!p.jobNumber) return 0;
                const match = p.jobNumber.match(/^(\d+)/);
                return match ? parseInt(match[1]) : 0;
              })
              .filter(num => num > 0 && num < 9000);
            const maxJobNumber = jobNumbers.length > 0 ? Math.max(...jobNumbers) : 0;
            setNextJobNumber(maxJobNumber + 1);

            return updated;
          });
        }
      } catch (err) {
        console.error('Error refreshing project:', err);
        // Fall back to full reload if single project fetch fails
        await loadInitialData();
      }
    }
  };

  const handleProjectDelete = async (projectId) => {
    try {
      await ProjectService.deleteProject(projectId);
      // Reload projects after deleting
      await loadInitialData();
    } catch (err) {
      console.error('Error deleting project:', err);
      throw err;
    }
  };

  const handleNewProjectClick = () => {
    setNewProjectModalOpen(true);
  };

  const handleNewProjectClose = () => {
    setNewProjectModalOpen(false);
  };

  const handleNewProjectSave = async () => {
    // Reload projects after creating new project
    await loadInitialData();
    setNewProjectModalOpen(false);
  };

  const handleQuoteCardClick = (quote) => {
    setSelectedQuote(quote);
    setQuoteModalOpen(true);
  };

  const handleQuoteModalClose = () => {
    setQuoteModalOpen(false);
    setSelectedQuote(null);
  };

  const handleQuoteModalSave = async () => {
    // Reload quotes after saving
    await loadInitialData();
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--dark-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--dark-bg)',
        padding: 'var(--space-6)'
      }}>
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--dark-bg)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-6) var(--space-6) var(--space-4)',
          backgroundColor: 'var(--dark-bg)',
          borderBottom: '1px solid var(--gray-600)',
          flexWrap: 'wrap',
          gap: 'var(--space-4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <Layout size={28} style={{ color: 'var(--gray-400)' }} />
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-3xl)',
                fontWeight: 'var(--font-bold)',
                color: 'var(--gray-50)',
              }}
            >
              Project Setup Board
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              {activeTab === 'waiting'
                ? `${waitingProjects.length} project${waitingProjects.length !== 1 ? 's' : ''} waiting to start`
                : activeTab === 'all'
                ? `${projects.length} total project${projects.length !== 1 ? 's' : ''}`
                : `${allModelQuotes.length} model quote${allModelQuotes.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: '0 var(--space-5)',
              background: 'var(--dark-elevated)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--gray-600)',
              height: '40px', // Match button height
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--gray-400)',
              }}
            >
              Next Job Number:
            </span>
            <span
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-bold)',
                color: 'var(--cta-blue)',
              }}
            >
              {nextJobNumber || 'N/A'}
            </span>
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={handleNewProjectClick}
          >
            <Plus size={20} style={{ marginRight: 'var(--space-2)' }} />
            New Project
          </Button>
        </div>
      </div>

      {/* Tabs and Search */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 var(--space-6)',
          backgroundColor: 'var(--dark-bg)',
          borderBottom: '2px solid var(--gray-700)',
          gap: 'var(--space-6)',
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--space-2)', paddingBottom: '2px', borderBottom: '2px solid transparent', marginBottom: '-2px' }}>
          <button
            onClick={() => setActiveTab('waiting')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-3) var(--space-5)',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'waiting' ? '3px solid var(--cta-blue)' : '3px solid transparent',
              color: activeTab === 'waiting' ? 'var(--cta-blue)' : 'var(--gray-400)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'waiting') {
                e.currentTarget.style.color = 'var(--gray-50)';
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'waiting') {
                e.currentTarget.style.color = 'var(--gray-400)';
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <Briefcase size={16} />
            Startup ({waitingProjects.length})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-3) var(--space-5)',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'all' ? '3px solid var(--cta-blue)' : '3px solid transparent',
              color: activeTab === 'all' ? 'var(--cta-blue)' : 'var(--gray-400)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'all') {
                e.currentTarget.style.color = 'var(--gray-50)';
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'all') {
                e.currentTarget.style.color = 'var(--gray-400)';
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            All Projects ({projects.length})
          </button>
          <button
            onClick={() => setActiveTab('model-quotes')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-3) var(--space-5)',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'model-quotes' ? '3px solid var(--cta-blue)' : '3px solid transparent',
              color: activeTab === 'model-quotes' ? 'var(--cta-blue)' : 'var(--gray-400)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'model-quotes') {
                e.currentTarget.style.color = 'var(--gray-50)';
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'model-quotes') {
                e.currentTarget.style.color = 'var(--gray-400)';
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <FileText size={16} />
            Model Quotes ({allModelQuotes.length})
          </button>
        </div>

        <div style={{ width: '300px', marginLeft: 'auto' }}>
          <SearchBar
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by job number, project name, client..."
            size="sm"
          />
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)' }}>
        {activeTab === 'model-quotes' ? (
          <div>
            {allModelQuotes.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--space-12)',
                  color: 'var(--gray-400)',
                }}
              >
                <FileText size={48} style={{ marginBottom: 'var(--space-4)' }} />
                <p style={{
                  fontSize: 'var(--text-lg)',
                  margin: 0,
                  fontWeight: 'var(--font-medium)'
                }}>
                  No model quotes found
                </p>
              </div>
            ) : (
              <ModelQuotesTable
                quotes={allModelQuotes}
                onRowClick={handleQuoteCardClick}
                hidePricing={true}
              />
            )}
          </div>
        ) : (
          <>
            {displayProjects.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--space-12)',
                  color: 'var(--gray-400)',
                }}
              >
                <Briefcase size={48} style={{ marginBottom: 'var(--space-4)' }} />
                <p style={{
                  fontSize: 'var(--text-lg)',
                  margin: 0,
                  fontWeight: 'var(--font-medium)',
                  marginBottom: 'var(--space-2)',
                }}>
                  No projects found
                </p>
                <p style={{
                  fontSize: 'var(--text-sm)',
                  margin: 0,
                  color: 'var(--gray-500)',
                }}>
                  {activeTab === 'waiting'
                    ? 'There are no projects waiting to start.'
                    : 'No projects available.'}
                </p>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 'var(--space-5)',
                  }}
                >
                  {displayProjects.map(project => (
                    <ProjectSetupCard
                      key={project.id}
                      project={project}
                      onUpdate={loadInitialData}
                      onClick={() => handleCardClick(project)}
                    />
                  ))}
                </div>
                {activeTab === 'all' && searchedProjects.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 'var(--space-6)',
                      padding: 'var(--space-4) var(--space-6)',
                      background: 'var(--dark-elevated)',
                      borderRadius: 'var(--radius-lg)',
                      border: '1px solid var(--gray-600)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--gray-400)',
                    }}
                  >
                    <span>
                      Showing {displayProjects.length} of {searchedProjects.length} projects
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                      <Button
                        variant={allProjectsLimit === 50 ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setAllProjectsLimit(50)}
                      >
                        50
                      </Button>
                      <Button
                        variant={allProjectsLimit === 100 ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setAllProjectsLimit(100)}
                      >
                        100
                      </Button>
                      <Button
                        variant={allProjectsLimit === 'all' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setAllProjectsLimit('all')}
                      >
                        All
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <ProjectSetupModal
        project={selectedProject}
        isOpen={modalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        onDelete={handleProjectDelete}
        clients={clients}
        contacts={contacts}
        nextJobNumber={nextJobNumber}
      />

      <NewProjectModal
        isOpen={newProjectModalOpen}
        onClose={handleNewProjectClose}
        onSave={handleNewProjectSave}
        clients={clients}
        contacts={contacts}
        nextJobNumber={nextJobNumber}
      />

      <ModelQuotesModalNoPricing
        quote={selectedQuote}
        clients={clients}
        contacts={contacts}
        isOpen={quoteModalOpen}
        onClose={handleQuoteModalClose}
        onSave={handleQuoteModalSave}
      />
    </div>
  );
};

export default ProjectSetupBoard;